import React, { useState, useEffect, useMemo } from "react";
import {
  ComposedChart,
  Area,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";

// import "../../../../administration/asset-stock/styles/AssetActivityChart.scss";
import "../../../styles/charts/AssetsCategoryChart.scss";

interface CategoryData {
  category: string;
  categoryId?: number;
  total: number;
  assigned: number;
  instock: number;
  inactive: number;
  child: CategoryData[];
  count?: number; // For root level compatibility
}

interface ChartDataPoint {
  name: string;
  categoryId: number | null;
  total: number;
  assigned: number;
  instock: number;
  inactive: number;
  hasChildren: boolean;
}

interface AssetCategoryChartProps {
  data: CategoryData[];
  itemsPerPage?: number;
}

const AssetCategoryChart: React.FC<AssetCategoryChartProps> = ({
  data,
  itemsPerPage = 5,
}) => {
  const [currentPage, setCurrentPage] = useState(1);
  const [navigationStack, setNavigationStack] = useState<
    Array<{
      categoryId: number | null;
      categoryName: string;
      data: CategoryData[];
    }>
  >([]);

  // Current level data based on navigation
  const currentLevelData = useMemo(() => {
    if (navigationStack.length === 0) {
      return data;
    }
    return navigationStack[navigationStack.length - 1].data;
  }, [data, navigationStack]);

  // Transform data for chart
  const chartData = useMemo((): ChartDataPoint[] => {
    return currentLevelData
      .map((item) => ({
        name: item.category,
        categoryId: item.categoryId || null,
        total: item.total || item.count || 0,
        assigned: item.assigned || 0,
        instock: item.instock || 0,
        inactive: item.inactive || 0,
        hasChildren: item.child && item.child.length > 0,
      }))
      .filter((item) => item.total > 0);
  }, [currentLevelData]);

  // Reset pagination when data changes
  useEffect(() => {
    setCurrentPage(1);
  }, [chartData]);

  // Pagination calculations
  const totalPages = Math.ceil(chartData.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const currentPageData = chartData.slice(
    startIndex,
    startIndex + itemsPerPage
  );

  // Handle category selection
  const handleCategoryClick = (
    categoryName: string,
    categoryId: number | null
  ) => {
    const clickedCategory = chartData.find(
      (item) => item.name === categoryName
    );

    if (clickedCategory && clickedCategory.hasChildren) {
      // Navigate to subcategory
      const categoryData = currentLevelData.find(
        (item) => item.category === categoryName
      );
      if (categoryData && categoryData.child) {
        setNavigationStack((prev) => [
          ...prev,
          {
            categoryId: categoryId,
            categoryName: categoryName,
            data: categoryData.child,
          },
        ]);
      }
    }
  };

  // Navigate back
  const handleNavigateBack = (index: number) => {
    if (index === 0) {
      setNavigationStack([]);
    } else {
      setNavigationStack((prev) => prev.slice(0, index));
    }
    setCurrentPage(1);
  };

  // Pagination handlers
  const handlePrevPage = () => setCurrentPage((prev) => Math.max(prev - 1, 1));
  const handleNextPage = () =>
    setCurrentPage((prev) => Math.min(prev + 1, totalPages));

  // Custom X-axis tick with click functionality
  const CustomXAxisTick = (props: any) => {
    const { x, y, payload } = props;
    const text = payload.value;
    const dataPoint = currentPageData.find((d) => d.name === text);
    const isClickable = dataPoint?.hasChildren || false;
    const maxLength = 15;
    const maxLines = 3;

    const lines = [];

    if (text.length > maxLength) {
      const words = text.split(" ");

      if (words.length > 1) {
        let currentLine = "";

        for (const word of words) {
          const testLine = currentLine ? `${currentLine} ${word}` : word;

          if (testLine.length <= maxLength) {
            currentLine = testLine;
          } else {
            if (currentLine) {
              lines.push(currentLine);
              if (lines.length >= maxLines - 1) {
                const remainingWords = words.slice(words.indexOf(word));
                const remainingText = remainingWords.join(" ");
                if (remainingText.length > maxLength) {
                  lines.push(remainingText.substring(0, maxLength - 3) + "...");
                } else {
                  lines.push(remainingText);
                }
                break;
              }
              currentLine = word;
            } else {
              if (word.length > maxLength) {
                lines.push(word.substring(0, maxLength - 3) + "...");
                break;
              } else {
                currentLine = word;
              }
            }
          }
        }

        if (currentLine && lines.length < maxLines) {
          lines.push(currentLine);
        }
      } else {
        const word = text;
        if (word.length > maxLength * maxLines) {
          lines.push(word.substring(0, maxLength - 3) + "...");
        } else if (word.length > maxLength * 2) {
          const partLength = Math.ceil(word.length / 3);
          lines.push(word.substring(0, partLength));
          lines.push(word.substring(partLength, partLength * 2));
          lines.push(word.substring(partLength * 2));
        } else if (word.length > maxLength) {
          const mid = Math.ceil(word.length / 2);
          lines.push(word.substring(0, mid));
          lines.push(word.substring(mid));
        } else {
          lines.push(word);
        }
      }
    } else {
      lines.push(text);
    }

    return (
      <g transform={`translate(${x},${y})`}>
        {lines.map((line, i) => (
          <text
            key={i}
            x={0}
            y={i * 14}
            fill={isClickable ? "#3b82f6" : "#6b7280"}
            textAnchor="middle"
            fontSize={11}
            dominantBaseline="hanging"
            style={{
              cursor: isClickable ? "pointer" : "default",
              fontWeight: isClickable ? "600" : "normal",
            }}
            onClick={() => {
              if (isClickable && dataPoint) {
                handleCategoryClick(text, dataPoint.categoryId);
              }
            }}>
            {line}
          </text>
        ))}
        {isClickable && (
          <text
            x={0}
            y={lines.length * 14 + 2}
            fill="#3b82f6"
            textAnchor="middle"
            fontSize={10}
            dominantBaseline="hanging"></text>
        )}
      </g>
    );
  };

  // Custom dot component (non-clickable for leaf nodes)
  const CustomDot = (props: any) => {
    const { cx, cy, stroke, fill, payload } = props;
    const isClickable = payload?.hasChildren || false;

    return (
      <circle
        cx={cx}
        cy={cy}
        r={4}
        fill={fill}
        stroke={stroke}
        strokeWidth={2}
        style={{
          cursor: isClickable ? "pointer" : "default",
          opacity: isClickable ? 1 : 0.7,
        }}
        onClick={() => {
          if (isClickable) {
            handleCategoryClick(payload.name, payload.categoryId);
          }
        }}
      />
    );
  };

  // Custom active dot
  const CustomActiveDot = (props: any) => {
    const { cx, cy, stroke, fill, payload } = props;
    const isClickable = payload?.hasChildren || false;

    return (
      <g>
        {isClickable && (
          <circle
            cx={cx}
            cy={cy}
            r={12}
            fill="transparent"
            style={{ cursor: "pointer" }}
            onClick={() =>
              handleCategoryClick(payload.name, payload.categoryId)
            }
          />
        )}
        <circle
          cx={cx}
          cy={cy}
          r={6}
          fill={fill}
          stroke={stroke}
          strokeWidth={3}
          style={{
            cursor: isClickable ? "pointer" : "default",
            opacity: isClickable ? 1 : 0.7,
          }}
          onClick={() => {
            if (isClickable) {
              handleCategoryClick(payload.name, payload.categoryId);
            }
          }}
        />
      </g>
    );
  };

  // Custom tooltip
  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      const data = payload[0]?.payload;
      const hasChildren = data?.hasChildren || false;

      return (
        <div className="asset-category-tooltip">
          <p className="tooltip-title">{label}</p>
          <div className="tooltip-item">
            <div className="tooltip-label">
              <div className="tooltip-dot total" />
              <span>Total</span>
            </div>
            <span className="tooltip-value">{data?.total || 0}</span>
          </div>
          <div className="tooltip-item">
            <div className="tooltip-label">
              <div className="tooltip-dot assigned" />
              <span>Assigned</span>
            </div>
            <span className="tooltip-value">{data?.assigned || 0}</span>
          </div>
          <div className="tooltip-item">
            <div className="tooltip-label">
              <div className="tooltip-dot instock" />
              <span>In Stock</span>
            </div>
            <span className="tooltip-value">{data?.instock || 0}</span>
          </div>
          <div className="tooltip-item">
            <div className="tooltip-label">
              <div className="tooltip-dot inactive" />
              <span>Inactive</span>
            </div>
            <span className="tooltip-value">{data?.inactive || 0}</span>
          </div>
          {hasChildren && (
            <div className="tooltip-footer">
              <small className="text-muted">
                <i className="ti ti-click me-1"></i>
                Click to drill down
              </small>
            </div>
          )}
        </div>
      );
    }
    return null;
  };

  return (
    <>
      {/* Legend */}
      <div className="chart-legend">
        <div className="legend-item">
          <div className="legend-dot total" />
          <span className="legend-text">Total Assets</span>
        </div>
        <div className="legend-item">
          <div className="legend-dot assigned" />
          <span className="legend-text">Assigned</span>
        </div>
        <div className="legend-item">
          <div className="legend-dot instock" />
          <span className="legend-text">In Stock</span>
        </div>
        <div className="legend-item">
          <div className="legend-dot inactive" />
          <span className="legend-text">Inactive</span>
        </div>
      </div>

      {/* Chart */}
      <div className="chart-wrapper">
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart
            data={currentPageData}
            margin={{ top: 10, right: 40, left: 20, bottom: 10 }}>
            <CartesianGrid
              strokeDasharray="2 4"
              stroke="#d1d5db"
              horizontal={true}
              vertical={true}
            />
            <XAxis
              dataKey="name"
              axisLine={false}
              tickLine={true}
              tick={<CustomXAxisTick />}
              height={60}
              interval={0}
            />
            <YAxis
              axisLine={false}
              tickLine={true}
              tick={{ fontSize: 13, fill: "#6b7280" }}
              width={40}
            />
            <Tooltip
              content={<CustomTooltip />}
              cursor={{
                stroke: "#d1d5db",
                strokeWidth: 1,
                strokeDasharray: "4 4",
              }}
            />
            <Area
              type="monotone"
              dataKey="instock"
              fill="rgba(16, 185, 129, 0.1)"
              stroke="none"
            />
            <Area
              type="monotone"
              dataKey="assigned"
              fill="rgba(59, 130, 246, 0.1)"
              stroke="none"
            />
            <Line
              type="monotone"
              dataKey="total"
              stroke="#8b5cf6"
              strokeWidth={2}
              dot={<CustomDot />}
              activeDot={<CustomActiveDot />}
            />
            <Line
              type="monotone"
              dataKey="assigned"
              stroke="#3b82f6"
              strokeWidth={2}
              dot={<CustomDot />}
              activeDot={<CustomActiveDot />}
            />
            <Line
              type="monotone"
              dataKey="instock"
              stroke="#10b981"
              strokeWidth={2}
              dot={<CustomDot />}
              activeDot={<CustomActiveDot />}
            />
            <Line
              type="monotone"
              dataKey="inactive"
              stroke="#ef4444"
              strokeWidth={2}
              dot={<CustomDot />}
              activeDot={<CustomActiveDot />}
            />
          </ComposedChart>
        </ResponsiveContainer>
        {/* Pagination and Controls */}
        <div className="chart-controls">
          <div className="pagination-info">
            <span className="text-muted">
              Showing {startIndex + 1}-
              {Math.min(startIndex + itemsPerPage, chartData.length)} of{" "}
              {chartData.length} categories
            </span>
          </div>
          <div className="pagination-controls">
            <button
              className="btn btn-outline-primary btn-sm"
              onClick={handlePrevPage}
              disabled={currentPage === 1}>
              <i className="ti ti-chevron-left" />
              Prev
            </button>
            <button
              className="btn btn-outline-primary btn-sm"
              onClick={handleNextPage}
              disabled={currentPage === totalPages}>
              Next
              <i className="ti ti-chevron-right" />
            </button>
            {navigationStack.length > 0 && (
              <button
                className="btn btn-outline-secondary btn-sm"
                onClick={() => handleNavigateBack(0)}>
                <i className="ti ti-refresh" />
                Reset
              </button>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default AssetCategoryChart;
